import React from 'react';

/**
 * Custom loading cell renderer for AG-Grid
 * Displays column-specific skeleton loaders based on the column field
 * @param {Object} props - Renderer props
 * @param {Object} props.colDef - Column definition containing field name
 * @returns {JSX.Element} Skeleton loading indicator appropriate for the column type
 */
const CustomLoadingCellRenderer = (props) => {
    const skeletonStyles = {
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        borderRadius: '4px'
    };

    const columnField = props.colDef?.field;
    const columnMinWidth = props.colDef?.minWidth || 100;

    // Render different skeletons based on column type
    const renderSingleSkeleton = () => {
        switch (columnField) {
            case 'campaignDisplayName':
                // Campaign column: arrow + name (2 lines)
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minHeight: '50px' }}>
                        <div style={{ 
                            ...skeletonStyles,
                            width: '20px',
                            height: '20px'
                        }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                            <div style={{ 
                                ...skeletonStyles,
                                width: `${Math.max(columnMinWidth - 50, 300)}px`,
                                height: '16px'
                            }} />
                            <div style={{ 
                                ...skeletonStyles,
                                width: `${Math.max(columnMinWidth - 50, 150)}px`,
                                height: '14px'
                            }} />
                        </div>
                    </div>
                );

            case 'status':
                // Status column: 3 status lines
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minHeight: '50px', justifyContent: 'center' }}>
                        <div style={{ 
                            ...skeletonStyles,
                            width: `${Math.max(columnMinWidth - 50, 100)}px`,
                            height: '14px'
                        }} />
                        <div style={{ 
                            ...skeletonStyles,
                            width: `${Math.max(columnMinWidth - 50, 100)}px`,
                            height: '14px'
                        }} />
                        <div style={{ 
                            ...skeletonStyles,
                            width: `${Math.max(columnMinWidth - 50, 100)}px`,
                            height: '14px'
                        }} />
                    </div>
                );

            case 'actions':
                // Actions column: 2 circular icons
                return (
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', minHeight: '50px', alignItems: 'center' }}>
                        <div style={{ 
                            ...skeletonStyles,
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%'
                        }} />
                        <div style={{ 
                            ...skeletonStyles,
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%'
                        }} />
                    </div>
                );

            default:
                // Other columns: single line skeleton based on column minWidth
                return (
                    <div style={{ display: 'flex', alignItems: 'center', minHeight: '50px' }}>
                        <div style={{ 
                            ...skeletonStyles,
                            width: `${Math.max(columnMinWidth - 50, 100)}px`,
                            height: '16px'
                        }} />
                    </div>
                );
        }
    };

    return (
        <>
            <style>
                {`
                    @keyframes shimmer {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                    }
                `}
            </style>
            <div>
                {[1, 2, 3, 4, 5].map((index) => (
                    <div 
                        key={index}
                        style={{
                            padding: '12px 0',
                            borderBottom: '1px solid #e0e0e0'
                        }}
                    >
                        {renderSingleSkeleton()}
                    </div>
                ))}
            </div>
        </>
    );
};

export default CustomLoadingCellRenderer;
